import React, { useState, useEffect } from "react";
import { useParams } from "react-router-dom";
import { FaSpinner } from "react-icons/fa";
import { tranzilaService, TranzilaPaymentRequest } from "../services/tranzila";
import { giftVoucherService, GiftVoucher } from "../services/firebase";
import { VOUCHER_PAYMENT_URLS } from "../config/tranzila";
import { useTranslation } from "react-i18next";
import "./Payment.css";

const VoucherPayment: React.FC = () => {
  const { voucherId } = useParams<{ voucherId: string }>();
  const { t, i18n } = useTranslation();
  const [voucher, setVoucher] = useState<GiftVoucher | null>(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [iframeUrl, setIframeUrl] = useState<string>("");

  useEffect(() => {
    const loadVoucher = async () => {
      if (!voucherId) {
        setError("מזהה שובר לא תקין");
        setLoading(false);
        return;
      }

      try {
        const voucherData = await giftVoucherService.getById(voucherId);
        setVoucher(voucherData);

        const description =
          i18n.language === "ru"
            ? `Подарочный сертификат (${voucherData.participants} участников)`
            : `שובר מתנה (${voucherData.participants} משתתפים)`;

        const paymentData: TranzilaPaymentRequest = {
          sum: voucherData.amountPaid,
          currency: "ILS",
          description,
          orderId: tranzilaService.generateOrderId(),
          customerName: voucherData.purchaserName,
          customerEmail: voucherData.purchaserEmail,
          customerPhone: voucherData.purchaserPhone,
          returnUrl: VOUCHER_PAYMENT_URLS.success(voucherId),
          cancelUrl: VOUCHER_PAYMENT_URLS.cancelled(voucherId),
          notifyUrl: VOUCHER_PAYMENT_URLS.notify(voucherId),
        };

        const validation = tranzilaService.validatePaymentData(paymentData);
        if (!validation.valid) {
          setError(validation.errors.join(", "));
          setLoading(false);
          return;
        }

        const response = await tranzilaService.createPayment(
          paymentData,
          i18n.language
        );

        if (response.success && response.iframeUrl) {
          await giftVoucherService.updatePaymentStatus(
            voucherId,
            "pending",
            response.transactionId,
            paymentData.orderId
          );
          setIframeUrl(response.iframeUrl);
          setLoading(false);
        } else {
          setError(response.error || "שגיאה ביצירת התשלום");
          setLoading(false);
        }
      } catch (err) {
                setError("שגיאה בטעינת פרטי השובר");
        setLoading(false);
      }
    };

    loadVoucher();
  }, [voucherId, i18n.language]);

  const handleIframeLoad = () => {
    setLoading(false);
  };

  const handleIframeError = () => {
    setError("שגיאה בטעינת טופס התשלום");
    setLoading(false);
  };

  if (loading) {
    return (
      <div className="page-container">
        <div className="loading-spinner">
          <FaSpinner className="fa-spin" />
          {t("loading")}{" "}
        </div>
      </div>
    );
  }

  if (error) {
    return (
      <div className="page-container">
        <div className="payment-card">
          <div className="payment-header">
            <h1 className="payment-title">שגיאה בתשלום</h1>
            <p className="payment-subtitle">{error}</p>
          </div>
        </div>
      </div>
    );
  }

  if (iframeUrl) {
    const { width, height } = tranzilaService.getIframeDimensions();
    const styles = tranzilaService.getIframeStyles();

    return (
      <div className="page-container">
        <div className="payment-card">
          <div className="payment-header">
            <h1 className="payment-title">{t("giftVoucherPageTitle")}</h1>
            <p className="payment-subtitle">
              {t("pleaseCompletePaymentDetails")}
            </p>
          </div>

          {voucher && (
            <div className="workshop-info">
              <h3 className="workshop-title">{t("giftVoucherPageTitle")}</h3>
              <div className="workshop-details">
                <span>
                  {t("giftVoucherPeopleLabel")}: {voucher.participants}
                </span>
                <div className="price">{voucher.amountPaid} ₪</div>
              </div>
            </div>
          )}

          <div className="iframe-container">
            <iframe
              src={iframeUrl}
              width={width}
              height={height}
              style={styles}
              onLoad={handleIframeLoad}
              onError={handleIframeError}
              title="Tranzila Payment Form"
              sandbox="allow-forms allow-scripts allow-same-origin allow-top-navigation"
              allow="payment"
            />
          </div>

          <div className="iframe-footer">
            <p className="security-note"> {t("securePaymentTranzila")}</p>
          </div>
        </div>
      </div>
    );
  }

  return (
    <div className="page-container">
      <div className="loading-spinner">
        <FaSpinner className="fa-spin" />
        {t("loading")}
      </div>
    </div>
  );
};

export default VoucherPayment;
